import { For, Show, createContext, createEffect, createSignal, onCleanup, useContext } from "solid-js";
import type { JSX } from "solid-js";
import type { Me } from "../api";
import type { Queue } from "../game/queue";
import type { Rematches } from "../game/rematches";
import { t } from "../strings";
import { toSignIn } from "../signin";
import { Avatar } from "./Avatar";
import { Brand } from "./Brand";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";
import { forBrowser } from "../pages/Link";

// The site's frame (design 14 §2, chess.com's): every page but the table, the replay and a lesson sits in it.
// On a wide screen a dark sidebar (the mark, then Play, History, Learn, Rules, the profile; Settings, About and
// Sign out at the bottom), collapsing to its icons below 1100 px; on a phone a top bar (the menu, the mark,
// Play) whose menu opens the same items as a drawer. The content is one column of one width.

export interface Site {
  me: () => Me | null | undefined; // undefined while the account loads, null signed out
  path: () => string;
  go: (p: string) => void;
  signOut: () => void;
  queue: Queue;
  rematches: Rematches;
  refreshMe: () => void;
  gameGoing: () => "turn" | "playing" | null; // a game of this account's is running
  backToGame: () => void;
}

export const SiteContext = createContext<Site>();

export function useSite(): Site {
  const s = useContext(SiteContext);
  if (!s) throw new Error("the frame needs SiteContext");
  return s;
}

interface Item {
  to: string;
  label: string;
  icon: IconName;
  match: (path: string) => boolean;
}

/** A page that has nothing to show: it says so, in the frame, with the way on. */
export function Gone(props: { text: string; go: (p: string) => void }) {
  return (
    <Frame title={t.notFound}>
      <p>{props.text}</p>
      <button type="button" onClick={() => props.go("/")}>
        {t.startYourOwn}
      </button>
    </Frame>
  );
}

/** A page in the frame: its title (in the same place on every page) and its content. */
export function Frame(props: { title?: string; class?: string; children: JSX.Element }) {
  const site = useSite();
  const [open, setOpen] = createSignal(false);
  // Open, the drawer has the keyboard: the focus goes in, Esc closes it and the focus comes back.
  let menuButton: HTMLButtonElement | undefined;
  let sidebar: HTMLElement | undefined;
  createEffect(() => {
    if (!open()) return;
    sidebar?.querySelector<HTMLElement>("a, button")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      menuButton?.focus();
    };
    addEventListener("keydown", onKey);
    onCleanup(() => removeEventListener("keydown", onKey));
  });
  const me = () => site.me() ?? null;
  const go = (to: string) => {
    setOpen(false);
    site.go(to);
  };
  const main = (): Item[] => [
    { to: "/", label: t.navHome, icon: "home", match: (p) => p === "/" || p.startsWith("/t/") },
    ...(me() ? [{ to: "/games", label: t.navHistory, icon: "history" as const, match: (p: string) => p === "/games" || p.startsWith("/g/") }] : []),
    { to: "/leaderboard", label: t.navBoard, icon: "board", match: (p) => p === "/leaderboard" },
    { to: "/learn", label: t.navLearn, icon: "learn", match: (p) => p === "/learn" || p.startsWith("/learn/") },
    { to: "/rules", label: t.navRules, icon: "rules", match: (p) => p === "/rules" || p === "/grading" },
  ];
  const link = (it: Item) => (
    <a
      href={it.to}
      class={`nav-item${it.match(site.path()) ? " here" : ""}`}
      aria-current={it.match(site.path()) ? "page" : undefined}
      onClick={(e) => {
        if (forBrowser(e)) return;
        e.preventDefault();
        go(it.to);
      }}
    >
      <Icon name={it.icon} class="nav-icon" />
      <span class="nav-label">{it.label}</span>
    </a>
  );
  return (
    <div class={`site${open() ? " drawer-open" : ""}`}>
      <a
        class="skip-link"
        href="#page"
        onClick={(e) => {
          e.preventDefault();
          const main = document.getElementById("page");
          main?.focus();
        }}
      >
        {t.skipToContent}
      </a>
      <header class="topbar">
        <button type="button" class="menu-button" ref={menuButton} aria-label={t.menu} aria-expanded={open()} onClick={() => setOpen((o) => !o)}>
          <Icon name="menu" class="nav-icon" />
        </button>
        <a
          href="/"
          class="topbar-brand"
          onClick={(e) => {
            if (forBrowser(e)) return;
            e.preventDefault();
            go("/");
          }}
        >
          <Brand size={30} />
        </a>
        <Show
          when={me()}
          fallback={
            <a class="button secondary topbar-action" href="/signin" onClick={toSignIn}>
              {t.signIn}
            </a>
          }
        >
          {/* It leads to Home, and did nothing there. */}
          <Show when={site.path() !== "/"}>
            <button type="button" class="secondary topbar-action" onClick={() => go("/")}>
              {t.play}
            </button>
          </Show>
        </Show>
      </header>
      <Show when={open()}>
        <div class="drawer-backdrop" onClick={() => setOpen(false)} />
      </Show>
      <nav class="sidebar" ref={sidebar} aria-label={t.menu}>
        <a
          href="/"
          class="sidebar-brand"
          onClick={(e) => {
            if (forBrowser(e)) return;
            e.preventDefault();
            go("/");
          }}
        >
          <Brand size={34} />
        </a>
        <div class="nav-main">
          <For each={main()}>{link}</For>
          <Show when={me()}>
            {(m) => (
              <a
                href={`/p/${m().public_id}`}
                class={`nav-item${site.path() === `/p/${m().public_id}` ? " here" : ""}`}
                onClick={(e) => {
                  if (forBrowser(e)) return;
                  e.preventDefault();
                  go(`/p/${m().public_id}`);
                }}
              >
                <Avatar id={m().avatar} size={24} />
                <span class="nav-label">{m().display_name}</span>
              </a>
            )}
          </Show>
        </div>
        <div class="nav-foot">
          <Show
            when={me()}
            fallback={
              <a class="button secondary sign-in" href="/signin" onClick={toSignIn}>
                {t.signIn}
              </a>
            }
          >
            <Show when={me()?.guest}>
              <a class="button secondary sign-in" href="/signin" onClick={toSignIn}>
                {t.signIn}
              </a>
            </Show>
            {link({ to: "/settings", label: t.settings, icon: "settings", match: (p) => p === "/settings" })}
          </Show>
          {link({ to: "/about", label: t.about, icon: "about", match: (p) => p === "/about" })}
          <Show when={me() && !me()!.guest}>
            <button type="button" class="nav-item" onClick={() => site.signOut()}>
              <Icon name="signout" class="nav-icon" />
              <span class="nav-label">{t.signOut}</span>
            </button>
          </Show>
        </div>
      </nav>
      <main id="page" tabindex="-1" class={`page site-main${props.class ? ` ${props.class}` : ""}`}>
        <Show when={site.gameGoing() && site.path() !== "/" ? site.gameGoing() : null}>
          {(going) => (
            <div class={`game-going${going() === "turn" ? " your-turn" : ""}`} role="status">
              <span>{going() === "turn" ? t.gameGoingTurn : t.gameGoing}</span>
              <button type="button" onClick={() => site.backToGame()}>
                {t.resume}
              </button>
            </div>
          )}
        </Show>
        <Show when={props.title}>
          <h1 class="page-title">{props.title}</h1>
        </Show>
        {props.children}
      </main>
    </div>
  );
}
